import { computed, defineComponent } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import {
    IEmployeeDetails,
    ITrackingReasonDetails,
    ITrackingTypeDetails
} from "../../../vue-interfaces/i-homecare-reassign";
import { ITypeaheadOptions } from "../../../vue-interfaces/i-typeahead";
import { IScheduleMatchRequest, TaskService } from "../../../../services/task.service";
import { AxxessIntelligenceService } from "../../../../services/axxess-intelligence.service";
import { ToastrService } from "../../../../services/toastr.service";
import { ApplicationEnum, ConflictType, ConflictTypeEnum, ConflictTypeNameEnum } from "../../../../enums/enums";
import { formatDateFromDate } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { IRescheduleValidations } from "../../../vue-dialogs/RescheduleValidations/RescheduleValidation";
import { IHandleTaskReschuleIssue } from "../../../vue-interfaces/i-visit-details";
import { IReassignVisitPayload } from "../../../vue-interfaces/i-reassign-visit";

import FormSection from "../../../vue-custom-elements/FormSection/FormSection.vue";
import MultiSelectDropdown from "../../../vue-custom-elements/MultiSelectDropdown/MultiSelectDropdown.vue";
import TypeaheadInput from "../../../vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import FindAMatch from "../../../vue-dialogs/FindAMatch/FindAMatch.vue";
import RescheduleValidation from "../../../vue-dialogs/RescheduleValidations/RescheduleValidation.vue";
import SaveNotifyUser from "../../../vue-custom-elements/SaveNotifyUser/SaveNotifyUser.vue";

export default defineComponent({
    components: {
        FormSection,
        MultiSelectDropdown,
        TypeaheadInput,
        SaveNotifyUser
    },

    props: {
        visitDetails: { type: Object, default: {} },
        isSaving: { type: Boolean, default: false },
        cancel: { type: Function, default: null },
        ok: { type: Function, default: null },
        reassign: { type: Function, default: null },
        preSelectedEmpId: { type: String, default: null }
    },

    data() {
        return {
            _toastrService: null,
            _taskService: null,
            _axxessIntelligenceService: null,
            employeeList: [] as IEmployeeDetails[],
            isLoading: false as boolean,
            scheduleEmployee: {} as ITypeaheadOptions,
            selectedConflictTypes: [ConflictTypeEnum.NoConflict] as number[],
            trackingTypes: [] as ITrackingTypeDetails[],
            trackingReason: [] as ITrackingReasonDetails[],
            cpyTrackingReason: [] as ITrackingReasonDetails[],
            isEmployeeListLoading: false as boolean,
            conflictTypes: [
                {
                    name: ConflictTypeNameEnum[ConflictTypeEnum.NoConflict],
                    value: ConflictTypeEnum.NoConflict,
                    type: "conflict-type"
                },
                {
                    name: ConflictTypeNameEnum[ConflictTypeEnum.PartialConflict],
                    value: ConflictTypeEnum.PartialConflict,
                    type: "conflict-type"
                },
                {
                    name: ConflictTypeNameEnum[ConflictTypeEnum.FullConflict],
                    value: ConflictTypeEnum.FullConflict,
                    type: "conflict-type"
                }
            ],
            employeeName: null as IEmployeeDetails,
            canShowPremium: true as boolean,
            dialogRef: null,
            formatDateFromDate,
            v$: useVuelidate()
        };
    },

    validations() {
        return {
            visitDetails: {
                TypeId: { required },
                ReasonId: { required },
                EmpId: { required }
            }
        };
    },

    watch: {
        selectedConflictTypes: {
            handler() {
                this.visitDetails.EmpId = null;
                this.getEmployeeList();
            },
            deep: true
        },
        scheduleEmployee(newValue: ITypeaheadOptions) {
            if (!!newValue) {
                this.visitDetails.EmpId = newValue.value;
            }
        }
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    async created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._axxessIntelligenceService = Container.instance.get(AxxessIntelligenceService);
        Object.assign(this.visitDetails, {});
        this.visitDetails.EmpId = null;
        this.visitDetails.TypeId = null;
        this.visitDetails.ReasonId = null;
        try {
            this.isLoading = true;
            await this.getEmployeeList();
            this.trackingReason = await this._taskService.getTrackingReasons({
                AgencyId: this.visitDetails.AgencyId,
                Application: this.visitDetails.Application,
                CategoryId: 1
            });
            this.trackingTypes = await this._taskService.getTrackingTypes({
                AgencyId: this.visitDetails.AgencyId,
                Application: this.visitDetails.Application,
                CategoryId: 1
            });
            const isPremiumResp = await this._axxessIntelligenceService.getAiPermissionByAgencies({
                AgencyIds: [this.visitDetails.AgencyId]
            });
            this.canShowPremium = isPremiumResp ? isPremiumResp.IsPremium : false;
        } catch (e) {
            console.error(e);
            this._toastrService.error({
                title: `Error Occurred`,
                message: `Some Error Occurred, Please contact Axxess.`
            });
            this.cancel();
        } finally {
            this.isLoading = false;
        }
        if (this.preSelectedEmpId) {
            this.visitDetails.EmpId = this.preSelectedEmpId;
            this.employeeName = this.employeeList.find((emp) => emp.Id === this.preSelectedEmpId);
        }
    },

    methods: {
        capitalize(text: string) {
            if (!text) return "";
            return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
        },
        async getEmployeeList() {
            try {
                this.isEmployeeListLoading = true;
                this.employeeList = await this._taskService.homecareEmployeePreference({
                    PatientId: this.visitDetails.PatientId,
                    AgencyId: this.visitDetails.AgencyId,
                    LocationId: this.visitDetails.LocationId,
                    StartDate: this.visitDetails.StartTime,
                    EndDate: this.visitDetails.EndTime,
                    SchedulerConflictTypes: this.selectedConflictTypes,
                    Application: ApplicationEnum.HomeCare
                });
                this.employeeList = this.employeeList.map((emp) => {
                    if (emp.SchedulerConflictType == ConflictType.enum.NoConflict) {
                        emp.Icon = `\uf058`;
                    } else if (emp.SchedulerConflictType == ConflictType.enum.PartialConflict) {
                        emp.Icon = `\uf06a`;
                    } else if (emp.SchedulerConflictType == ConflictType.enum.FullConflict) {
                        emp.Icon = `\uf057`;
                    } else {
                        emp.Icon = "";
                    }
                    return emp;
                });
            } catch (e) {
                throw e;
            } finally {
                this.isEmployeeListLoading = false;
            }
        },

        formatTime(dateTime: Date) {
            return dateTime
                ? new Date(dateTime).toLocaleTimeString("en-us", {
                      hour: "2-digit",
                      minute: "2-digit"
                  })
                : "";
        },

        selectedType() {
            if (!!this.visitDetails.TypeId) {
                this.cpyTrackingReason = this.trackingReason.filter((reasonType) => {
                    return reasonType.TypeId == (this.visitDetails.TypeId as any);
                });
            } else {
                this.visitDetails.ReasonId = null;
                this.cpyTrackingReason = [];
            }
        },

        selectedItemsUpdated(value: any) {
            if (this.selectedConflictTypes.includes(value)) {
                const index = this.selectedConflictTypes.indexOf(value);
                this.selectedConflictTypes.splice(index, 1);
                return;
            }
            this.selectedConflictTypes.push(value);
        },

        employeeListFilter(filter: string) {
            if (filter) {
                const filterEmployeeList = this.employeeList.filter((employee) => {
                    return employee.DisplayName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterEmployeeList?.map((item) => {
                    return {
                        name: `${item.DisplayName}`,
                        value: item.Id
                    };
                });
            } else {
                return this.employeeList?.map((item) => ({
                    value: item.Id,
                    name: `${item.DisplayName}`
                }));
            }
        },

        changescheduleEmployee(data: any) {
            this.scheduleEmployee = data;
        },

        async reassignVisit(notifyUser: boolean = false) {
            const valid = await this.v$.$validate();
            if (!valid) {
                return;
            }
            let reassignDetails = Object.assign({}, this.visitDetails);
            if (notifyUser) {
                if (!this.visitDetails?.EmpId) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `Please select employee to notify.`
                    });
                    return;
                }
                reassignDetails.NotifyUser = true;
                reassignDetails.PatientName = `${this.visitDetails.FirstName} ${this.visitDetails.LastName}`;
                reassignDetails.VisitDate = this.formatDateFromDate(
                    this.visitDetails.StartDateTime || this.visitDetails.StartDate
                );
                reassignDetails.VisitTime = this.visitDetails.StartDateTime || this.visitDetails.StartTime;
            }
            const response = await this.reassign(reassignDetails);
            if (!response.isSuccessful && response.errorDetails) {
                if (!response.errorDetails.taskIssues.length) {
                    throw new Error(response.errorMessage);
                }

                const validationList: IRescheduleValidations[] = response.errorDetails.taskIssues.map((item: any) => {
                    return {
                        title: item.title as string,
                        description: [item.message] as string[],
                        taskIssueSurpressKey: item.taskIssueSurpressKey as string
                    };
                });
                const hasError = response.errorDetails.allTasksHaveErrors;

                this.dialogRef = this.$dialog.open(RescheduleValidation, {
                    props: {
                        modal: true,
                        closeOnEscape: false,
                        showHeader: false,
                        style: {
                            width: "60vw"
                        }
                    },

                    data: {
                        validations: validationList,
                        name: "Employee",
                        component: "Reassign-Visit",
                        hasError
                    },

                    onClose: async (options: any) => {
                        if (options.data == "cancel") {
                            return;
                        } else {
                            const issuesResponseObj: IHandleTaskReschuleIssue = {};
                            for (let issue of validationList) {
                                issuesResponseObj[issue.taskIssueSurpressKey] = "ScheduleAllAnyways";
                            }
                            let reassignPayload: IReassignVisitPayload = {
                                AccountId: reassignDetails.AgencyId,
                                UserId: reassignDetails.EmpId,
                                Application: reassignDetails.Application,
                                EpisodeId: reassignDetails.EpisodeId,
                                PatientId: reassignDetails.PatientId,
                                TaskId: reassignDetails.Id,
                                TypeId: reassignDetails.TypeId,
                                ReasonId: reassignDetails.ReasonId
                            };
                            if (notifyUser) {
                                reassignPayload.NotifyUser = true;
                                reassignPayload.PatientName = `${this.visitDetails.FirstName} ${this.visitDetails.LastName}`;
                                reassignPayload.VisitDate = this.formatDateFromDate(
                                    this.visitDetails.StartDateTime || this.visitDetails.StartDate
                                );
                                reassignPayload.VisitTime =
                                    this.visitDetails.StartDateTime || this.visitDetails.StartTime;
                            }
                            this.$emit("update:isSaving", true);
                            const secondResponse = await this._taskService.reassignVisit({
                                ...reassignPayload,
                                handleTaskIssues: issuesResponseObj
                            });
                            if (!secondResponse.isSuccessful) {
                                this.$emit("update:isSaving", false);
                                throw new Error(secondResponse.errorMessage);
                            } else {
                                this._toastrService.success({
                                    message: `Visit has been successfully reassigned.`,
                                    title: `Visit Reassigned`
                                });
                                this.ok();
                                this.$emit("update:isSaving", false);
                            }
                        }
                    }
                });
            } else {
                this._toastrService.success({
                    title: `Visit Reassigned`,
                    message: `Visit has been successfully reassigned.`
                });
                this.ok();
            }
        },
        async openFindAMatch() {
            let clientName = "";
            if (this.visitDetails.UserFirstName && this.visitDetails.UserLastName) {
                clientName = `${this.visitDetails.UserFirstName},${this.visitDetails.UserLastName}`;
            }
            if (this.visitDetails.fromUnassignedPage) {
                clientName = this.visitDetails.FirstName;
            }
            const payload: IScheduleMatchRequest = {
                application: this.visitDetails.Application,
                agencyId: this.visitDetails.AgencyId,
                clientName: clientName,
                endDate: this.visitDetails.fromUnassignedPage ? this.visitDetails.EndDate : this.visitDetails.EndTime,
                startDate: this.visitDetails.fromUnassignedPage
                    ? this.visitDetails.StartDate
                    : this.visitDetails.StartTime,
                isAllDay: false,
                locationId: this.visitDetails.LocationId,
                patientId: this.visitDetails.PatientId,
                payor: "",
                task: this.visitDetails.TaskName
            };

            this.dialogRef = this.$dialog.open(FindAMatch, {
                props: {
                    modal: true,
                    closeOnEscape: false,
                    style: {
                        width: "82vw"
                    },
                    showHeader: false
                },

                data: {
                    payload
                },

                onClose: async (options) => {
                    if (options.data) {
                        this.employeeName = this.employeeList.find((emp) => emp.Id === options.data.userId);
                        this.scheduleEmployee = {
                            value: options.data.userId,
                            name: options.data.displayName
                        };
                    }
                }
            });
        }
    }
});
